import { initializeApp } from 'firebase/app';
import { browserLocalPersistence, connectAuthEmulator, initializeAuth } from 'firebase/auth';

/**
 * Firebase project configuration.
 *
 * These values are not secrets — Firebase's own docs are explicit about this:
 * the web API key identifies which project a request belongs to, it does not
 * authorise access to it. Access control happens through Firebase Auth
 * providers and, on our side, through the ID token every API request carries.
 * Committing them is the documented, normal thing to do; the only thing that
 * must never be committed is a *service account* key, which this app does
 * not use anywhere in the browser.
 */
const firebaseConfig = {
  apiKey: 'AIzaSyALD-noeQpf5wJAzIJ_BoBtalAgq6FMb9g',
  authDomain: 'fire-tracker-abhishek.firebaseapp.com',
  // Overridable so the e2e run can point at the emulator's demo project. The
  // token's iss/aud derive from this, and the API checks them, so client and
  // Worker have to agree on it.
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? 'fire-tracker-abhishek',
  storageBucket: 'fire-tracker-abhishek.firebasestorage.app',
  messagingSenderId: '642519308836',
  appId: '1:642519308836:web:c2b2c2af03e3b2cfc716b1',
};

const app = initializeApp(firebaseConfig);

/**
 * `initializeAuth`, not `getAuth`.
 *
 * `getAuth()` is the convenient entry point, and it pays for that convenience
 * by wiring in every dependency a Firebase app *might* need: the popup and
 * redirect resolver, and the IndexedDB persistence layer. This app signs in
 * with an email and a password and nothing else — no Google button, no phone
 * number, no reCAPTCHA — so all of that machinery ships to every visitor and
 * is never once called.
 *
 * Declaring the dependencies explicitly lets the bundler drop what is not
 * named here. `popupRedirectResolver` is deliberately absent rather than set
 * to undefined; naming it at all would pull it back in.
 *
 * The persistence change is a real one worth stating: local storage instead of
 * IndexedDB. Sessions still survive a closed tab, which is the property that
 * matters. IndexedDB is the more capable store, but nothing here needs that
 * capability, and it is not worth its weight on every first load.
 */
export const auth = initializeAuth(app, { persistence: browserLocalPersistence });

/*
 * Point at the Auth emulator when one is configured.
 *
 * Only the e2e suite sets this. `import.meta.env` is inlined at build time, so
 * a production build has the literal `undefined` here and the whole block is
 * dropped by the bundler — the emulator connection cannot be turned on at
 * runtime by anything a visitor controls.
 */
const emulatorHost = import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_HOST;
if (emulatorHost) {
  connectAuthEmulator(auth, `http://${emulatorHost}`, { disableWarnings: true });
}
